import { useEffect } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  action: () => void;
  description: string;
}

/**
 * useKeyboardShortcuts Hook
 * Gère les raccourcis clavier globaux de l'application
 * Note: Désactivé sur mobile/tablette pour éviter les conflits avec les gestes tactiles
 */
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  useEffect(() => {
    // Disable keyboard shortcuts on touch devices to prevent conflicts with swipe gestures
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      return; // Exit early on mobile/tablet
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore shortcuts when typing in inputs/textareas
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      for (const shortcut of shortcuts) {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;

        if (keyMatch && ctrlMatch && altMatch && shiftMatch) {
          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
}

/**
 * Show keyboard shortcuts help modal content
 */
export function getShortcutsHelp(shortcuts: KeyboardShortcut[]): string {
  return shortcuts
    .map(s => {
      const keys: string[] = [];
      if (s.ctrl) keys.push('Ctrl');
      if (s.alt) keys.push('Alt');
      if (s.shift) keys.push('Shift');
      keys.push(s.key.toUpperCase());
      return `${keys.join(' + ')}: ${s.description}`;
    })
    .join('\n');
}
